/**
 * Learning Center API: the people directory and the person summary (plan
 * docs/plans/lms-plan.md Appendix A "Groups and directory"; 8.2, 4.2, 4.9,
 * 18.20; WP-32's routes, WP-43's client). Typed calls over services/api (the
 * shared axios instance that adds the JWT and X-Site-Id); nothing under
 * src/lms imports axios (2.3a). Both routes are lms admin routes.
 *
 *   GET /admin/directory?q=&site_id=&limit=&offset=
 *       -> { items: [{ user_id, full_name, email, role, is_active, sites: [{id, name}], is_lms_admin }], page }
 *   GET /admin/people/:userId
 *       -> { user, sites, groups, assignments, waivers, completions, external_certs, signoffs, onboarding_runs }
 *
 * Reach (4.9, G14, 18.21): only people who reach a site the acting admin
 * reaches are listed; anyone else is 404 "Person not found", the same answer as
 * an unknown id. The person pickers (components/pickers/UserPicker) read the
 * directory; WP-45's person page reads the summary.
 *
 * Query strings travel as axios `params`, so the URL itself stays the path
 * (the test harness routes GETs by exact URL).
 */
import api from '../../services/api';
import type { LmsList, LmsPlatformRole } from './client';

const enc = encodeURIComponent;

/** Appendix A: a list page holds at most 200 rows. */
export const DIRECTORY_MAX_LIMIT = 200;

/** A site a person reaches, as the directory names it. */
export interface DirectorySite {
  id: string;
  name: string | null;
}

/** One GET /admin/directory item (WP-32 groups/directory.js). */
export interface DirectoryPerson {
  user_id: string;
  full_name: string | null;
  email: string | null;
  role: LmsPlatformRole | null;
  /** false for a deactivated account (still listed; a send skips them, 8.2). */
  is_active: boolean;
  /** The admin-reachable sites this person reaches, nothing beyond (G14). */
  sites: DirectorySite[];
  /** super_admin, administrator, or the lms admin tool role at one of those sites (4.2). */
  is_lms_admin: boolean;
}

export interface DirectoryQuery {
  /** A case-insensitive substring of the name or the email (at most 100 characters). */
  q?: string;
  /** Narrow to one reachable site (404 "Site not found" outside the admin's reach). */
  siteId?: string | null;
  limit?: number;
  offset?: number;
}

/** GET /api/lms/admin/directory */
export async function listDirectory(query: DirectoryQuery = {}): Promise<LmsList<DirectoryPerson>> {
  const params: Record<string, string | number> = {};
  const q = (query.q ?? '').trim();
  if (q) params.q = q;
  if (query.siteId) params.site_id = query.siteId;
  if (query.limit !== undefined) params.limit = Math.min(query.limit, DIRECTORY_MAX_LIMIT);
  if (query.offset !== undefined) params.offset = query.offset;
  const response = await api.get<LmsList<DirectoryPerson>>('/api/lms/admin/directory', { params });
  return response.data;
}

/** A course named on a person's record (groups/common.js courseSummary). */
export interface PersonCourseRef {
  id: string;
  title: string;
  slug: string;
  category: string;
}

/**
 * GET /admin/people/:userId (WP-32; 18.20). The record lists keep the rows'
 * own columns (assignments, completions, external certificates, sign-offs,
 * runs), each with a `course` reference where it names one; typed loosely
 * here so a reader (WP-45's person page) narrows what it shows.
 */
export interface PersonSummary {
  user: {
    id: string;
    full_name: string | null;
    email: string | null;
    role: LmsPlatformRole | null;
    is_active: boolean;
    /** true for a soft-deleted person whose records stay readable (G7). */
    deleted: boolean;
  };
  sites: DirectorySite[];
  groups: Array<{ id: string; name: string; kind: 'profile' | 'adhoc'; is_active: boolean; added_at: string }>;
  assignments: Array<Record<string, unknown> & { id: string; course: PersonCourseRef | null }>;
  waivers: Array<Record<string, unknown> & { id: string; course: PersonCourseRef | null }>;
  completions: Array<
    Record<string, unknown> & {
      id: string;
      course: PersonCourseRef | null;
      certificate: { id: string; code: string } | null;
      state: 'current' | 'expired' | 'superseded';
    }
  >;
  external_certs: Array<Record<string, unknown> & { id: string; course: PersonCourseRef | null }>;
  signoffs: Array<Record<string, unknown> & { id: string; course: PersonCourseRef | null; observer_name: string | null }>;
  onboarding_runs: Array<Record<string, unknown> & { id: string; template_name: string | null }>;
}

/** GET /api/lms/admin/people/:userId */
export async function getPersonSummary(userId: string): Promise<PersonSummary> {
  const response = await api.get<PersonSummary>(`/api/lms/admin/people/${enc(userId)}`);
  return response.data;
}
